import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { QUIZ_CIRCUITS, shuffle, getOptions } from '../constants/quizCircuits';
import useGameChallenge from '../hooks/useGameChallenge';
import ChallengeInviteModal from '../components/ChallengeInviteModal';
import ChallengeDuelBanner from '../components/ChallengeDuelBanner';
import RivalryCard from '../components/RivalryCard';
import Icon from '../components/Icon';

const TOTAL = QUIZ_CIRCUITS.length;

const QUIZ_TIERS = [
  { maxMs:  20000, xp: 2000, label: '< 20 sec',   color: '#e10600' },
  { maxMs:  30000, xp: 1000, label: '< 30 sec',   color: '#f97316' },
  { maxMs:  60000, xp:  250, label: '< 1 min',    color: '#facc15' },
  { maxMs:  90000, xp:  100, label: '< 1 min 30', color: '#a3e635' },
  { maxMs: 120000, xp:    0, label: 'Pas assez rapide !', color: '#71717a' },
  { maxMs: Infinity, xp:  0, label: 'Pas assez rapide !', color: '#71717a' },
];
const getTier = (ms) => QUIZ_TIERS.find(t => ms < t.maxMs) || QUIZ_TIERS.at(-1);

const fmt = (ms) => {
  if (ms === null || ms === undefined) return null;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  return m > 0
    ? `${m}:${String(sec).padStart(2, '0')}.${tenths}`
    : `${sec}.${tenths}s`;
};

const Quiz = () => {
  const { user, refreshUser } = useAuth();

  const [phase, setPhase]         = useState('idle');
  const [queue, setQueue]         = useState([]);
  const [index, setIndex]         = useState(0);
  const [options, setOptions]     = useState([]);
  const [unsolved, setUnsolved]   = useState(new Set());
  const [solvedCount, setSolvedCount] = useState(0);
  const [passNumber, setPassNumber]   = useState(1);
  const [elapsed, setElapsed]     = useState(0);
  const [feedback, setFeedback]   = useState(null);
  const [chosenId, setChosenId]   = useState(null);
  const [result, setResult]       = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myBest, setMyBest]       = useState(null);
  const [imgError, setImgError]     = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const { activeChallenge, opponent, myScore, opponentScore, hasSubmitted, isCompleted, iWon, submitScore, submitting: cSubmitting, refresh: refreshChallenge } = useGameChallenge('cirquiz');

  const startTimeRef   = useRef(null);
  const stopwatchRef   = useRef(null);
  const feedbackRef    = useRef(null);

  const fetchLeaderboard = useCallback(() => {
    api.get('/game/quiz/leaderboard').then(r => setLeaderboard(r.data)).catch(() => {});
    if (user) api.get('/game/quiz/best').then(r => setMyBest(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);
  useEffect(() => () => {
    clearInterval(stopwatchRef.current);
    clearTimeout(feedbackRef.current);
  }, []);

  const startStopwatch = () => {
    startTimeRef.current = Date.now();
    stopwatchRef.current = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 100);
  };

  const stopStopwatch = () => {
    clearInterval(stopwatchRef.current);
    return Date.now() - startTimeRef.current;
  };

  const startGame = () => {
    clearTimeout(feedbackRef.current);
    clearInterval(stopwatchRef.current);
    const shuffled = shuffle(QUIZ_CIRCUITS);
    setQueue(shuffled);
    setIndex(0);
    setOptions(getOptions(shuffled[0], QUIZ_CIRCUITS));
    setUnsolved(new Set(QUIZ_CIRCUITS.map(c => c.id)));
    setSolvedCount(0);
    setPassNumber(1);
    setElapsed(0);
    setFeedback(null);
    setChosenId(null);
    setResult(null);
    setImgError(false);
    setPhase('playing');
    startStopwatch();
  };

  const handleAnswer = (option) => {
    if (feedback) return;
    const current = queue[index];
    const isCorrect = option.id === current.id;

    setChosenId(option.id);
    setFeedback(isCorrect ? 'correct' : 'wrong');

    const newUnsolved = new Set(unsolved);
    let newSolved = solvedCount;
    if (isCorrect) {
      newUnsolved.delete(current.id);
      newSolved = solvedCount + 1;
      setUnsolved(newUnsolved);
      setSolvedCount(newSolved);
    }

    feedbackRef.current = setTimeout(() => {
      setFeedback(null);
      setChosenId(null);
      setImgError(false);
      const nextIdx = index + 1;

      if (nextIdx >= queue.length) {
        if (newUnsolved.size === 0) {
          const finalMs = stopStopwatch();
          const tier = getTier(finalMs);
          setResult({ ms: finalMs, xpEarned: tier.xp, tier });
          setPhase('finished');
          if (user) {
            api.post('/game/quiz', { time: finalMs })
              .then(() => refreshUser())
              .catch(() => {});
            setTimeout(fetchLeaderboard, 1000);
          }
        } else {
          const newQueue = shuffle(QUIZ_CIRCUITS.filter(c => newUnsolved.has(c.id)));
          setQueue(newQueue);
          setIndex(0);
          setOptions(getOptions(newQueue[0], QUIZ_CIRCUITS));
          setPassNumber(p => p + 1);
        }
      } else {
        setIndex(nextIdx);
        setOptions(getOptions(queue[nextIdx], QUIZ_CIRCUITS));
      }
    }, 400);
  };

  const currentCircuit = queue[index];
  const progressPct = (solvedCount / TOTAL) * 100;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Box-Box · CirQuiz</p>
        <h1 className="text-3xl font-black text-white">CirQuiz</h1>
        <p className="text-zinc-400 text-sm mt-1">Identifie tous les circuits le plus vite possible.</p>
      </div>

      <ChallengeDuelBanner
        activeChallenge={activeChallenge}
        opponent={opponent}
        myScore={myScore}
        opponentScore={opponentScore}
        hasSubmitted={hasSubmitted}
        isCompleted={isCompleted}
        iWon={iWon}
        myLiveScore={phase === 'finished' ? result?.ms ?? null : null}
        canSubmit={phase === 'finished' && result !== null && !hasSubmitted}
        formatScore={fmt}
        onSubmit={() => submitScore(result.ms)}
        submitting={cSubmitting}
        game="cirquiz"
      />
      <RivalryCard opponent={opponent} game="cirquiz" />

      {/* ── IDLE ── */}
      {phase === 'idle' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-white font-black text-lg mb-4">Comment jouer ?</h2>
            <ol className="space-y-3 text-zinc-400 text-sm">
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">1.</span> Clique sur <strong className="text-white">Commencer</strong> — le chrono se lance.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">2.</span> Un tracé s'affiche avec <strong className="text-white">3 noms de GP</strong>. Choisis le bon.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">3.</span> Bonne réponse → circuit suivant. Mauvaise → circuit suivant <strong className="text-white">sans voir la réponse</strong>.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">4.</span> Une fois tous les circuits passés, les <strong className="text-white">circuits ratés reviennent</strong> jusqu'à ce que tu les trouves tous.</li>
              <li className="flex gap-3"><span className="text-f1red font-black shrink-0">5.</span> Le chrono s'arrête quand tu as <strong className="text-white">identifié tous les {TOTAL} circuits</strong>. Score = temps total.</li>
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              {QUIZ_TIERS.map(t => (
                <span key={t.maxMs} className="text-xs font-bold px-2.5 py-1 rounded-full border"
                  style={{ borderColor: `${t.color}44`, color: t.color, backgroundColor: `${t.color}12` }}>
                  {t.label} → {t.xp} XP
                </span>
              ))}
            </div>
            <button onClick={startGame} className="btn-primary w-full mt-6 py-3 text-base">
              Commencer
            </button>
            {user && (
              <button onClick={() => setShowChallengeModal(true)}
                className="w-full mt-2 py-2.5 rounded-xl text-sm font-bold border border-zinc-700 text-zinc-300 hover:border-f1red/50 hover:text-white transition-all">
                <Icon name="sword" size={13} className="shrink-0" /> Affronter un ami
              </button>
            )}
          </div>
          <LeaderboardSection leaderboard={leaderboard} myBest={myBest} user={user} />
        </div>
      )}

      {/* ── PLAYING ── */}
      {phase === 'playing' && currentCircuit && (
        <div className="space-y-4">
          {/* Header: chrono + progress */}
          <div className="flex items-center justify-between gap-4">
            <span className="text-3xl font-black tabular-nums text-white">{fmt(elapsed)}</span>
            <div className="flex-1 flex flex-col gap-1">
              <div className="flex justify-between text-xs text-zinc-500">
                <span>{passNumber > 1 ? `Passe ${passNumber} — erreurs` : `Passe 1`}</span>
                <span className="text-green-400 font-bold">{solvedCount}/{TOTAL}</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>

          {/* Circuit image */}
          <div className="relative bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden"
            style={{ minHeight: '220px' }}>
            {feedback === 'correct' && (
              <div className="absolute inset-0 bg-green-500/20 z-10 flex items-center justify-center rounded-2xl pointer-events-none">
                <span className="text-5xl font-black text-green-400">✓</span>
              </div>
            )}
            {feedback === 'wrong' && (
              <div className="absolute inset-0 bg-red-500/20 z-10 flex items-center justify-center rounded-2xl pointer-events-none">
                <span className="text-5xl font-black text-red-400">✗</span>
              </div>
            )}
            {!imgError ? (
              <img key={currentCircuit.id} src={currentCircuit.img} alt="Circuit tracé"
                className="w-full h-full object-contain p-4" style={{ maxHeight: '280px' }}
                onError={() => setImgError(true)} />
            ) : (
              <div className="flex items-center justify-center h-56 text-zinc-600 text-sm flex-col gap-2">
                <Icon name="flag" size={32} className="text-zinc-700" />
                <span>Tracé non disponible</span>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 gap-2">
            {options.map(opt => {
              const isChosen = chosenId === opt.id;
              let cls = 'border-zinc-700 hover:border-zinc-500 text-zinc-200 hover:bg-zinc-800/50';
              if (feedback === 'correct' && isChosen) cls = 'border-green-500 bg-green-500/10 text-green-300';
              else if (feedback === 'wrong' && isChosen) cls = 'border-red-500 bg-red-500/10 text-red-300';
              return (
                <button key={opt.id} onClick={() => handleAnswer(opt)} disabled={!!feedback}
                  className={`w-full text-left px-4 py-3 rounded-xl border font-semibold text-sm transition-all ${cls} disabled:cursor-default`}>
                  {opt.gpName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── FINISHED ── */}
      {phase === 'finished' && result && (
        <div className="space-y-6">
          <div className="card text-center py-8">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-3">Temps final</p>
            <p className="text-6xl font-black text-white mb-1 tabular-nums">{fmt(result.ms)}</p>
            <p className="text-zinc-500 text-sm mb-4">{TOTAL} circuits identifiés</p>
            {myBest?.best && result.ms < myBest.best && (
              <p className="text-green-400 text-xs font-bold mb-3 flex items-center gap-1"><Icon name="trophy" size={12} /> Nouveau record personnel !</p>
            )}
            <div className="inline-flex flex-col items-center gap-1 px-6 py-3 rounded-2xl border mb-4"
              style={{ borderColor: `${result.tier.color}44`, backgroundColor: `${result.tier.color}12` }}>
              <span className="text-2xl font-black" style={{ color: result.tier.color }}>
                +{result.xpEarned} XP
              </span>
              <span className="text-xs" style={{ color: result.tier.color }}>{result.tier.label}</span>
            </div>
            {!user && (
              <p className="text-zinc-500 text-xs mb-4">
                <Link to="/login" className="text-f1red underline">Connecte-toi</Link> pour sauvegarder ton temps.
              </p>
            )}
            <div className="flex justify-center gap-3">
              <button onClick={startGame} className="btn-primary px-8 py-2.5">Rejouer</button>
            </div>
          </div>
          <LeaderboardSection leaderboard={leaderboard} myBest={myBest} user={user} />
        </div>
      )}
    {showChallengeModal && (
      <ChallengeInviteModal game="cirquiz"
        onClose={() => setShowChallengeModal(false)}
        onSent={() => { setShowChallengeModal(false); refreshChallenge(); }} />
    )}
    </div>
  );
};

const LeaderboardSection = ({ leaderboard, myBest, user }) => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="card">
      <h3 className="text-white font-black mb-4 flex items-center gap-2"><Icon name="trophy" size={14} /> Meilleurs temps</h3>
      {leaderboard.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-6">Aucun score pour l'instant.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {leaderboard.slice(0, 10).map((entry, i) => (
            <div key={entry._id || i}
              className={`flex items-center gap-3 px-3 py-2 rounded-xl ${i === 0 ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-zinc-800/40'}`}>
              <span className={`text-sm font-black w-5 text-center shrink-0 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-orange-400' : 'text-zinc-600'}`}>
                {i + 1}
              </span>
              {entry.avatar
                ? <img src={entry.avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                : <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-black text-zinc-300 shrink-0">
                    {entry.username?.[0]?.toUpperCase()}
                  </div>
              }
              <span className="flex-1 text-white text-sm font-semibold truncate">{entry.username}</span>
              <span className="text-white font-black text-sm tabular-nums">{fmt(entry.best)}</span>
            </div>
          ))}
        </div>
      )}
    </div>

    <div className="card">
      <h3 className="text-white font-black mb-4">📊 Mon meilleur</h3>
      {!user ? (
        <p className="text-zinc-500 text-sm text-center py-6">Connecte-toi pour suivre tes temps.</p>
      ) : !myBest?.best ? (
        <p className="text-zinc-500 text-sm text-center py-6">Aucune partie jouée.</p>
      ) : (
        <div className="text-center py-4">
          <p className="text-5xl font-black text-white mb-1 tabular-nums">{fmt(myBest.best)}</p>
          <p className="text-zinc-500 text-sm">meilleur temps</p>
          <p className="text-zinc-600 text-xs mt-2">{myBest.plays} partie{myBest.plays !== 1 ? 's' : ''} jouée{myBest.plays !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  </div>
);

export default Quiz;
